import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { AlertTriangle, Bell, CheckCheck, Info, OctagonAlert } from "lucide-react";
import type { NotificationDto, NotificationsResponse } from "@theadam/shared";
import { Button } from "@/components/ui/button";
import { Alert, Card, Spinner } from "@/components/ui/misc";
import { api } from "@/lib/api";
import { cn, formatDateTime } from "@/lib/utils";
import { ChannelLogo } from "@/components/ChannelLogo";

export const NOTIFICATIONS_KEY = ["notifications"] as const;

export function useNotifications() {
  return useQuery({ queryKey: NOTIFICATIONS_KEY, queryFn: () => api.get<NotificationsResponse>("/notifications"), refetchInterval: 30_000, refetchIntervalInBackground: true });
}

const ICON = { info: Info, warning: AlertTriangle, critical: OctagonAlert } as const;
const TONE = { info: "text-info", warning: "text-warning", critical: "text-danger" } as const;

export function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useNotifications();

  const open = async (n: NotificationDto) => {
    if (n.status === "unread") {
      await api.post(`/notifications/${n.id}/read`);
      await qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    }
    if (n.link) navigate(n.link);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    await qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="text-sm text-muted-foreground" aria-label="Konum">
            <Link to="/" className="hover:text-foreground">
              Genel Bakış
            </Link>{" "}
            / <span className="text-foreground">Bildirimler</span>
          </nav>
          <h1 className="mt-1 text-2xl font-semibold">Bildirimler</h1>
          <p className="mt-1 text-sm text-muted-foreground">Yeni sipariş, tükenen stok ve kanal senkron sorunları burada görünür.</p>
        </div>
        {q.data && q.data.unread > 0 && (
          <Button variant="outline" onClick={readAll}>
            <CheckCheck /> Tümünü okundu say
          </Button>
        )}
      </div>
      {q.isLoading && <Spinner />}
      {q.error && <Alert tone="danger" title="Bildirimler yüklenemedi" />}
      {q.data && q.data.items.length === 0 && (
        <Card className="p-10 text-center">
          <Bell className="mx-auto size-6 text-muted-foreground" aria-hidden />
          <p className="mt-2 font-medium">Henüz bildirim yok</p>
          <p className="mt-1 text-sm text-muted-foreground">Kanallardan yeni sipariş geldiğinde veya bir ürünün stoğu bittiğinde burada göreceksiniz.</p>
        </Card>
      )}
      <ul className="space-y-2">
        {q.data?.items.map((n) => {
          const Icon = ICON[n.severity];
          return (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => open(n)}
                className={cn("flex w-full gap-3 rounded-lg border bg-card p-4 text-left text-sm hover:bg-muted/50", n.status === "unread" && "border-l-4 border-l-accent")}
              >
                {n.channel ? (
                  <span className="relative mt-0.5 shrink-0">
                    <ChannelLogo channel={n.channel} size="md" />
                    <Icon className={cn("absolute -right-1.5 -bottom-1.5 size-4 rounded-full bg-card", TONE[n.severity])} aria-hidden />
                  </span>
                ) : (
                  <Icon className={cn("mt-0.5 size-4 shrink-0", TONE[n.severity])} aria-hidden />
                )}
                <span className="min-w-0 flex-1">
                  <span className={cn("block", n.status === "unread" && "font-semibold")}>{n.title}</span>
                  {n.body && <span className="mt-0.5 block text-muted-foreground">{n.body}</span>}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{formatDateTime(n.createdAt)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
